console.log("varient-media file chal rahi hai");



const FILTER_ENABLED_SELECTOR = 'media-gallery[data-variant-media-filter-enabled="true"]';
const HIDDEN_CLASS = 'is-hidden';
const DEBUG_PREFIX = '[VMF]';
const STRICT_STRUCTURED_MODE = true;

const KEY_ALIASES = {
  colour: 'color',
  colours: 'color',
  colors: 'color',
  sizes: 'size',
  packs: 'pack',
  quantity: 'pack',
  quantities: 'pack',
};

const EVENT_NAMES = ['variant:update', 'variant:change', 'variantChange', 'product:variant-change', 'shopify:section:load'];

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\s*[-–—]\s*unavailable\s*$/i, '')
    .replace(/\s*\(\s*unavailable\s*\)\s*$/i, '')
    .replace(/[^a-z0-9*]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export function normalizeValue(value) {
  return normalizeText(value);
}

export function normalizeKey(value) {
  const normalized = normalizeText(value);
  return KEY_ALIASES[normalized] || normalized;
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function containsNormalizedValue(haystack, needle) {
  if (!haystack || !needle) return false;

  if (!/\d/.test(needle)) {
    return haystack.includes(needle);
  }

  const regex = new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(needle)}(?:$|[^a-z0-9])`);
  return regex.test(haystack);
}

function buildKnownKeySet(optionNames = []) {
  const set = new Set();

  for (const optionName of optionNames) {
    const key = normalizeKey(optionName);
    if (key) set.add(key);
  }

  return set;
}

function coerceOptionKey(key, knownKeySet = new Set()) {
  const normalized = normalizeKey(key);
  if (!normalized) return '';

  if (!knownKeySet.size) {
    return normalized;
  }

  if (knownKeySet.has(normalized)) {
    return normalized;
  }

  const compact = normalized.replace(/\s+/g, '');
  for (const known of knownKeySet) {
    if (known.replace(/\s+/g, '') === compact) {
      return known;
    }
  }

  const alias = KEY_ALIASES[normalized];
  if (alias && knownKeySet.has(alias)) {
    return alias;
  }

  return normalized;
}

export function parseOptionNames(raw) {
  if (!raw) return [];

  const candidates = [raw, raw.replace(/&quot;/g, '"')];

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      if (Array.isArray(parsed)) {
        return parsed.map((name) => normalizeKey(name)).filter(Boolean);
      }
    } catch {
      // noop
    }
  }

  return [];
}

export function parseAltRules(alt, knownKeySet = new Set()) {
  const source = String(alt || '');
  const pipeIndex = source.indexOf('|');

  if (pipeIndex < 0) {
    return {
      hasStructuredRules: false,
      rules: {},
      source,
    };
  }

  const rulesText = source.slice(pipeIndex + 1).trim();
  if (!rulesText || !rulesText.includes(':')) {
    return {
      hasStructuredRules: false,
      rules: {},
      source,
    };
  }

  const rules = {};

  for (const segment of rulesText.split(';')) {
    const trimmed = segment.trim();
    if (!trimmed) continue;

    const splitAt = trimmed.indexOf(':');
    if (splitAt < 1) continue;

    const rawKey = trimmed.slice(0, splitAt).trim();
    const rawValues = trimmed.slice(splitAt + 1).trim();

    if (!rawKey || !rawValues) continue;

    const key = coerceOptionKey(rawKey, knownKeySet);
    if (!key) continue;

    const values = rawValues
      .split(',')
      .map((value) => normalizeValue(value))
      .filter(Boolean);

    if (!values.length) continue;

    if (!rules[key]) rules[key] = [];

    for (const value of values) {
      if (!rules[key].includes(value)) {
        rules[key].push(value);
      }
    }
  }

  return {
    hasStructuredRules: Object.keys(rules).length > 0,
    rules,
    source,
  };
}

export function scoreStructuredRules(rules, selectedOptions) {
  const keys = Object.keys(rules);
  if (!keys.length) return -1;

  let score = 0;

  for (const key of keys) {
    const selectedValue = selectedOptions[key];
    if (!selectedValue) return -1;

    const allowedValues = rules[key] || [];
    if (allowedValues.includes('*')) {
      score += 1;
      continue;
    }

    const matches = allowedValues.some((value) => value === selectedValue);
    if (!matches) return -1;

    score += 2;
  }

  return score;
}

export function inferAltScore(alt, selectedOptions) {
  const altNormalized = normalizeValue(alt || '');
  if (!altNormalized) return 0;

  const values = [...new Set(Object.values(selectedOptions || {}).filter(Boolean))];
  if (!values.length) return 0;

  let score = 0;

  for (const value of values) {
    if (containsNormalizedValue(altNormalized, value)) {
      score += 1;
    }
  }

  return score;
}

export function computeVisibilityPlan(items, selectedOptions) {
  const diagnostics = items.map((item) => ({
    index: item.index,
    alt: item.alt || '',
    rules: item.parsed.rules,
    expectedVisible: false,
    score: 'untagged',
    strategy: 'none',
  }));

  const structuredIndexes = [];
  let hasStructuredRules = false;

  items.forEach((item, itemIndex) => {
    if (item.parsed.hasStructuredRules) {
      hasStructuredRules = true;
      structuredIndexes.push(itemIndex);
    }
  });

  if (hasStructuredRules) {
    let bestStructuredScore = -1;

    for (const itemIndex of structuredIndexes) {
      const item = items[itemIndex];
      const score = scoreStructuredRules(item.parsed.rules, selectedOptions);
      diagnostics[itemIndex].score = score;
      diagnostics[itemIndex].strategy = 'structured';

      if (score > bestStructuredScore) {
        bestStructuredScore = score;
      }
    }

    if (bestStructuredScore >= 0) {
      diagnostics.forEach((diag) => {
        diag.expectedVisible = diag.score === bestStructuredScore;
      });

      return {
        mode: 'structured',
        visibleIndexes: diagnostics.filter((diag) => diag.expectedVisible).map((diag) => diag.index),
        diagnostics,
      };
    }

    if (STRICT_STRUCTURED_MODE) {
      diagnostics.forEach((diag) => {
        diag.expectedVisible = false;
        if (diag.strategy === 'none') {
          diag.strategy = 'structured-strict';
          diag.score = -1;
        }
      });

      return {
        mode: 'structured-strict',
        visibleIndexes: [],
        diagnostics,
      };
    }
  }

  let bestInferScore = 0;

  diagnostics.forEach((diag, itemIndex) => {
    const inferScore = inferAltScore(items[itemIndex].alt, selectedOptions);
    diag.score = inferScore;
    diag.strategy = 'inferred';

    if (inferScore > bestInferScore) {
      bestInferScore = inferScore;
    }
  });

  if (bestInferScore > 0) {
    diagnostics.forEach((diag) => {
      diag.expectedVisible = Number(diag.score) === bestInferScore;
    });

    return {
      mode: 'inferred',
      visibleIndexes: diagnostics.filter((diag) => diag.expectedVisible).map((diag) => diag.index),
      diagnostics,
    };
  }

  diagnostics.forEach((diag) => {
    diag.expectedVisible = true;
    diag.score = 'fallback-all';
    diag.strategy = 'fallback-all';
  });

  return {
    mode: 'fallback-all',
    visibleIndexes: diagnostics.map((diag) => diag.index),
    diagnostics,
  };
}

function resolveScope(element) {
  return (
    element?.closest('.shopify-section') ||
    element?.closest('[data-product-id]') ||
    element?.closest('main') ||
    document
  );
}

function resolveOptionKey(control, optionKeys) {
  const knownKeySet = new Set(optionKeys);

  const dataOptionName = control.getAttribute('data-option-name');
  if (dataOptionName) {
    return coerceOptionKey(dataOptionName, knownKeySet);
  }

  const dataInputId = control.getAttribute('data-input-id');
  if (dataInputId) {
    const position = Number.parseInt(dataInputId.split('-')[0], 10);
    if (!Number.isNaN(position) && optionKeys[position - 1]) {
      return optionKeys[position - 1];
    }
  }

  const rawName = control.getAttribute('name') || '';
  const optionMatch = rawName.match(/^options\[(.+)\]$/i);
  if (optionMatch?.[1]) {
    return coerceOptionKey(optionMatch[1], knownKeySet);
  }

  if (rawName && optionKeys.length) {
    const normalizedName = normalizeKey(rawName);

    if (knownKeySet.has(normalizedName)) {
      return normalizedName;
    }

    for (const optionKey of optionKeys) {
      if (normalizedName.startsWith(optionKey)) {
        return optionKey;
      }
    }
  }

  const legend = control.closest('fieldset')?.querySelector('legend');
  if (legend?.textContent) {
    return coerceOptionKey(legend.textContent, knownKeySet);
  }

  return '';
}

function getControlValue(control) {
  if (control instanceof HTMLSelectElement) {
    const selected = control.options[control.selectedIndex];
    return normalizeValue(selected?.value || control.value || selected?.textContent || '');
  }

  return normalizeValue(control.value || '');
}

function readSelectedOptionsFromDom(scope, optionKeys) {
  const selected = {};

  const selector = [
    'variant-picker input[type="radio"]:checked',
    'variant-picker select',
    '.option-color-swatches .option-selector-input:checked',
    '.option-selector-input:checked',
    '.variant-option__select',
    '[name^="options["]',
  ].join(', ');

  const controls = scope.querySelectorAll(selector);

  controls.forEach((control) => {
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;

    const key = resolveOptionKey(control, optionKeys);
    const value = getControlValue(control);

    if (!key || !value) return;
    selected[key] = value;
  });

  return selected;
}

function readSelectedOptionsFromVariantResource(resource, optionKeys) {
  const selected = {};

  if (!resource || typeof resource !== 'object') {
    return selected;
  }

  for (let i = 0; i < optionKeys.length; i += 1) {
    const key = optionKeys[i];
    const value = resource[`option${i + 1}`];

    if (!key || typeof value !== 'string') continue;

    const normalized = normalizeValue(value);
    if (normalized) selected[key] = normalized;
  }

  return selected;
}

function extractVariantResource(event) {
  const detail = event && 'detail' in event ? event.detail : null;
  if (!detail) return null;

  if (detail.resource && typeof detail.resource === 'object') return detail.resource;
  if (detail.variant && typeof detail.variant === 'object') return detail.variant;
  if (detail.selectedVariant && typeof detail.selectedVariant === 'object') return detail.selectedVariant;
  if (typeof detail === 'object') return detail;

  return null;
}

function escapeCssValue(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(String(value));
  }

  return String(value).replace(/"/g, '\\"');
}

function getSelectedVariantId(scope) {
  const variantIdInput = scope.querySelector(
    [
      '[data-type="add-to-cart-form"] [name="id"]',
      '[data-type="add-to-cart-form"] .product-variant-id',
      'input[name="id"]',
      '.product-variant-id',
    ].join(', ')
  );

  if (variantIdInput && typeof variantIdInput.value === 'string') {
    const value = variantIdInput.value.trim();
    if (value) return value;
  }

  return '';
}

function readVariantResourceFromScripts(scope, productId, variantId) {
  if (!variantId) return null;

  const selectors = [];
  if (productId) {
    selectors.push(`script[data-variants][data-product-id="${escapeCssValue(productId)}"]`);
  }
  selectors.push('script[data-variants]');

  for (const selector of selectors) {
    const scripts = scope.querySelectorAll(selector);

    for (const script of scripts) {
      try {
        const variants = JSON.parse(script.textContent || '[]');
        if (!Array.isArray(variants)) continue;

        const variant = variants.find((item) => String(item?.id) === String(variantId));
        if (variant) return variant;
      } catch {
        // noop
      }
    }
  }

  return null;
}

function resolveVariantResource(scope, gallery, event, selectedFromDom, optionKeys) {
  const fromEvent = extractVariantResource(event);
  if (fromEvent) return fromEvent;

  // INSTANT MATCH: Find variant directly from the newly selected DOM options
  if (selectedFromDom && optionKeys && Object.keys(selectedFromDom).length > 0) {
    const scripts = scope.querySelectorAll('script[data-variants]');
    for (const script of scripts) {
      try {
        const variants = JSON.parse(script.textContent || '[]');
        const matchedVariant = variants.find(v => {
          for (let i = 0; i < optionKeys.length; i++) {
            const key = optionKeys[i];
            const domValue = selectedFromDom[key];
            if (domValue) {
              const variantValue = normalizeValue(v[`option${i+1}`]);
              // Agar mismatch hai tou reject kar do
              if (variantValue && variantValue !== domValue) {
                return false;
              }
            }
          }
          return true; // Match found!
        });
        if (matchedVariant) return matchedVariant;
      } catch (e) {}
    }
  }

  // Fallback to old behavior (reading hidden input ID)
  const variantId = String(getSelectedVariantId(scope) || '');
  return readVariantResourceFromScripts(scope, gallery.dataset.productId || '', variantId);
}

function resolveFeaturedMediaId(resource) {
  const id =
    resource?.featured_media?.id ||
    resource?.featured_image?.id ||
    resource?.featured_media_id ||
    resource?.image_id ||
    '';

  return id ? String(id) : '';
}

function resolvePrimaryEntry(entries, featuredMediaId = '', visibleIndexes = []) {
  if (!entries.length) return null;

  if (featuredMediaId) {
    const featuredEntry = entries.find((entry) => String(entry.mediaId) === String(featuredMediaId));
    if (featuredEntry) return featuredEntry;
  }

  // If we already know which indexes the filter plan wants visible, prefer the
  // first of those — not the currently-active DOM slide (which may be from the
  // old variant).
  if (visibleIndexes.length > 0) {
    const firstVisible = entries.find((entry) => visibleIndexes.includes(entry.index));
    if (firstVisible) return firstVisible;
  }

  const activeSlideEntry = entries.find((entry) =>
    entry.nodes.slides.some((slide) => !slide.hasAttribute('hidden') && slide.getAttribute('aria-hidden') === 'false')
  );
  if (activeSlideEntry) return activeSlideEntry;

  const firstVisibleSlideEntry = entries.find((entry) => entry.nodes.slides.some((slide) => !slide.hasAttribute('hidden')));
  if (firstVisibleSlideEntry) return firstVisibleSlideEntry;

  return entries[0];
}

function promoteGridMediaToFront(gallery, mediaId) {
  if (!mediaId) return;

  const grid = gallery.querySelector('.media-gallery__grid');
  if (!(grid instanceof HTMLElement)) return;

  const selector = `.media-gallery__grid > li[data-media-id="${escapeCssValue(mediaId)}"]`;
  const target = gallery.querySelector(selector);

  if (!(target instanceof HTMLElement)) return;
  if (grid.firstElementChild === target) return;

  grid.insertBefore(target, grid.firstElementChild);
}

function setHiddenState(element, hidden) {
  if (!(element instanceof HTMLElement)) return;

  if (hidden) {
    element.classList.add(HIDDEN_CLASS);
    element.setAttribute('hidden', '');

    if (element.matches('slideshow-slide, .media-gallery__grid > li, .dialog-zoomed-gallery > li')) {
      element.setAttribute('aria-hidden', 'true');
      if (element.matches('slideshow-slide')) {
        element.removeAttribute('reveal');
      }
    }

    if (element.matches('button')) {
      element.setAttribute('aria-selected', 'false');
    }

    return;
  }

  element.classList.remove(HIDDEN_CLASS);
  element.removeAttribute('hidden');

  if (!element.matches('slideshow-slide')) {
    element.removeAttribute('aria-hidden');
  }
}

function collectEntries(gallery) {
  const entryMap = new Map();
  const optionKeys = parseOptionNames(gallery.dataset.optionNames || '');
  const knownKeySet = buildKnownKeySet(optionKeys);

  const buckets = [
    ['slides', 'slideshow-slide[data-media-index]'],
    ['grid', '.media-gallery__grid > li[data-media-index]'],
    ['zoom', '.dialog-zoomed-gallery > li[data-media-index]'],
    ['zoomThumb', '.dialog-thumbnails-list__thumbnail[data-media-index]'],
    ['thumb', '.slideshow-controls__thumbnail[data-media-index]'],
    ['dot', '.slideshow-controls__dots button[data-media-index], button[ref="dots[]"][data-media-index]'],
  ];

  function ensureEntry(index) {
    if (!entryMap.has(index)) {
      entryMap.set(index, {
        index,
        mediaId: '',
        alt: '',
        parsed: {
          hasStructuredRules: false,
          rules: {},
          source: '',
        },
        nodes: {
          slides: [],
          grid: [],
          zoom: [],
          zoomThumb: [],
          thumb: [],
          dot: [],
        },
      });
    }

    return entryMap.get(index);
  }

  for (const [bucket, selector] of buckets) {
    const nodes = gallery.querySelectorAll(selector);

    nodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      const index = Number.parseInt(node.dataset.mediaIndex || '', 10);
      if (Number.isNaN(index)) return;

      const entry = ensureEntry(index);
      entry.nodes[bucket].push(node);

      if (!entry.mediaId && node.dataset.mediaId) entry.mediaId = node.dataset.mediaId;
      if (!entry.alt && node.dataset.mediaAlt) entry.alt = node.dataset.mediaAlt;

      if (!entry.alt) {
        const nestedAlt = node.querySelector('img[alt]')?.getAttribute('alt');
        if (nestedAlt) entry.alt = nestedAlt;
      }

      if (entry.alt && !entry.parsed.source) {
        entry.parsed = parseAltRules(entry.alt, knownKeySet);
      }
    });
  }

  const entries = [...entryMap.values()].sort((a, b) => a.index - b.index);

  entries.forEach((entry) => {
    if (!entry.parsed.source && entry.alt) {
      entry.parsed = parseAltRules(entry.alt, knownKeySet);
    }
  });

  return {
    entries,
    optionKeys,
  };
}

function remapControls(entries) {
  const visible = entries.filter((entry) => entry.visible).sort((a, b) => a.index - b.index);
  const visiblePositionMap = new Map(visible.map((entry, visiblePosition) => [entry.index, visiblePosition]));

  entries.forEach((entry) => {
    const visiblePosition = visiblePositionMap.get(entry.index);
    if (visiblePosition === undefined) return;

    for (const control of entry.nodes.thumb) {
      control.setAttribute('on:click', `/select/${visiblePosition}`);
    }

    for (const control of entry.nodes.dot) {
      control.setAttribute('on:click', `/select/${visiblePosition}`);
    }

    for (const control of entry.nodes.zoomThumb) {
      control.setAttribute('on:click', `/handleThumbnailClick/${entry.index}`);
      control.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${entry.index}`);
    }
  });
}

function updateCounterTotals(gallery, entries) {
  const visibleCount = entries.filter((entry) => entry.visible).length;
  const counters = gallery.querySelectorAll('.slideshow-controls__counter');

  counters.forEach((counter) => {
    const current = counter.querySelector('[ref="current"]');
    if (current && Number.parseInt(current.textContent || '1', 10) > visibleCount) {
      current.textContent = '1';
    }

    const slash = counter.querySelector('.slash');
    if (!slash) return;

    const trailing = slash.nextSibling;
    if (trailing && trailing.nodeType === Node.TEXT_NODE) {
      trailing.textContent = String(visibleCount || 1);
    } else {
      counter.append(document.createTextNode(String(visibleCount || 1)));
    }
  });
}

// ─── FIX: Suppress slideshow transition animations during variant swap ────────
// This prevents the "flash of old image" by making show/hide instant.
// We add a no-transition class, apply all DOM changes, then remove it after
// one paint so normal user interactions stay animated.
function withNoTransition(gallery, fn) {
  const style = document.createElement('style');
  const galleryId = gallery.dataset.productId || 'vmf';
  const attr = `data-vmf-notransition`;
  gallery.setAttribute(attr, '');
  
  // Added slider container selectors and scroll-behavior: auto
  style.textContent = `
    [${attr}],
    [${attr}] slideshow-component,
    [${attr}] .slider,
    [${attr}] slideshow-slide,
    [${attr}] slideshow-slide *,
    [${attr}] .media-gallery__grid > li,
    [${attr}] .media-gallery__grid > li * {
      transition: none !important;
      animation: none !important;
      scroll-behavior: auto !important;
    }
  `;
  document.head.appendChild(style);

  fn();

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      gallery.removeAttribute(attr);
      style.remove();
    });
  });
}

function syncSlideshowSelection(gallery, entries, preferredMediaId = '') {
  const slideshow = gallery.querySelector('slideshow-component');
  if (!slideshow || typeof slideshow.select !== 'function') return;

  const visibleEntries = entries.filter((entry) => entry.visible);
  if (!visibleEntries.length) return;

  const currentVisible = visibleEntries.find((entry) =>
    entry.nodes.slides.some((slide) => slide.getAttribute('aria-hidden') === 'false')
  );

  const preferredVisible = preferredMediaId
    ? visibleEntries.find((entry) => String(entry.mediaId) === String(preferredMediaId))
    : null;

  const target = preferredVisible || currentVisible || visibleEntries[0];
  if (!target || !target.mediaId) return;

  slideshow.select({ id: String(target.mediaId) }, undefined, { animate: false });
}

function isDebugEnabled() {
  if (typeof window === 'undefined') return false;

  if (window.__VMF_DEBUG__ === true) return true;

  try {
    if (window.localStorage?.getItem('vmf_debug') === '1') return true;
  } catch {
    // noop
  }

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('vmf_debug') === '1') return true;
  } catch {
    // noop
  }

  return false;
}

function stringifyRules(rules) {
  const keys = Object.keys(rules || {});
  if (!keys.length) return '(none)';

  return keys.map((key) => `${key}:${(rules[key] || []).join(',')}`).join(';');
}

function logDebug(gallery, trigger, selectedOptions, plan, entries) {
  if (!isDebugEnabled()) return;

  const visibleSlides = [...gallery.querySelectorAll('slideshow-slide[data-media-index]:not([hidden])')].map((slide) =>
    Number.parseInt(slide.getAttribute('data-media-index') || '-1', 10)
  );

  const visibleGrid = [...gallery.querySelectorAll('.media-gallery__grid > li[data-media-index]:not([hidden])')].map((item) =>
    Number.parseInt(item.getAttribute('data-media-index') || '-1', 10)
  );

  const rows = plan.diagnostics.map((diag, rowIndex) => ({
    index: diag.index,
    mediaId: entries[rowIndex]?.mediaId || '',
    score: diag.score,
    expectedVisible: diag.expectedVisible,
    alt: diag.alt,
    rules: stringifyRules(diag.rules),
  }));

  console.log(`${DEBUG_PREFIX} event:${trigger}`);
  console.log('selectedOptions:', selectedOptions);
  console.log('expectedVisible:', plan.visibleIndexes);
  console.log('actualVisible(slides):', visibleSlides);
  console.log('actualVisible(grid):', visibleGrid);
  console.table(rows);
}

function applyGalleryFilter(gallery, trigger, event) {
  const { entries, optionKeys } = collectEntries(gallery);
  if (!entries.length) return;

  const scope = resolveScope(gallery);
  const selectedFromDom = readSelectedOptionsFromDom(scope, optionKeys);

  // Pass DOM options to resolve the variant instantly without delay
  const variantResource = resolveVariantResource(scope, gallery, event, selectedFromDom, optionKeys);
  const selectedFromVariant = readSelectedOptionsFromVariantResource(variantResource, optionKeys);

  // CRITICAL FIX: Merge order changed! 
  // selectedFromDom MUST override selectedFromVariant because DOM has the absolute latest user selection.
  const selectedOptions = {
    ...selectedFromVariant,
    ...selectedFromDom,
  };

  const plan = computeVisibilityPlan(entries, selectedOptions);
  const visibleSet = new Set(plan.visibleIndexes);
  const featuredMediaId = resolveFeaturedMediaId(variantResource);

  // Determine the primary (featured) entry for slideshow navigation.
  // IMPORTANT: Only add it to visibleSet if:
  //   (a) it was explicitly set via featuredMediaId from the variant resource, OR
  //   (b) the filter plan produced zero visible items (pure fallback scenario).
  // We must NOT force-add the currently-active slide from the DOM when the filter
  // plan already knows which images belong to the new variant — doing so is what
  // causes the old variant's image to linger for 1-2 seconds after a swap.
  const primaryEntry = resolvePrimaryEntry(entries, featuredMediaId, plan.visibleIndexes);
  const primaryMediaId = primaryEntry?.mediaId || featuredMediaId;

  const filterPlanHasResults = visibleSet.size > 0;

  if (primaryEntry) {
    // Only add to visible if the featured media was explicitly identified from
    // the variant resource, OR there are no filter-plan results at all.
    const primaryIsFromFeaturedMedia =
      featuredMediaId && String(primaryEntry.mediaId) === String(featuredMediaId);

    if (primaryIsFromFeaturedMedia || !filterPlanHasResults) {
      visibleSet.add(primaryEntry.index);
      if (!plan.visibleIndexes.includes(primaryEntry.index)) {
        plan.visibleIndexes = [...plan.visibleIndexes, primaryEntry.index].sort((a, b) => a - b);
      }
    }
  }

  const selectedKeys = Object.keys(selectedOptions).sort();
  const selectionSignature = selectedKeys.map((key) => `${key}:${selectedOptions[key]}`).join('|');
  const visibleSignature = [...visibleSet].sort((a, b) => a - b).join(',');
  const entriesSignature = `${entries.length}:${entries[0]?.mediaId || ''}:${entries[entries.length - 1]?.mediaId || ''}`;
  const signature = `${selectionSignature}#${primaryMediaId || ''}#${visibleSignature}#${entriesSignature}`;
  const previousSignature = galleryStateCache.get(gallery);

  if (previousSignature === signature && trigger !== 'mutation' && trigger !== 'shopify:section:load') {
    return;
  }

  withNoTransition(gallery, () => {
    entries.forEach((entry, entryIndex) => {
      const visible = visibleSet.has(entry.index);
      entry.visible = visible;

      const nodes = [
        ...entry.nodes.slides,
        ...entry.nodes.grid,
        ...entry.nodes.zoom,
        ...entry.nodes.zoomThumb,
        ...entry.nodes.thumb,
        ...entry.nodes.dot,
      ];

      nodes.forEach((node) => setHiddenState(node, !visible));
      plan.diagnostics[entryIndex].expectedVisible = visible;
    });

    if (!entries.some((entry) => entry.visible)) {
      entries[0].visible = true;

      const fallbackNodes = [
        ...entries[0].nodes.slides,
        ...entries[0].nodes.grid,
        ...entries[0].nodes.zoom,
        ...entries[0].nodes.zoomThumb,
        ...entries[0].nodes.thumb,
        ...entries[0].nodes.dot,
      ];

      fallbackNodes.forEach((node) => setHiddenState(node, false));
    }

    remapControls(entries);
    updateCounterTotals(gallery, entries);
    promoteGridMediaToFront(gallery, primaryMediaId);
    syncSlideshowSelection(gallery, entries, primaryMediaId);
  });

  galleryStateCache.set(gallery, signature);
  logDebug(gallery, trigger, selectedOptions, plan, entries);
}

function applyAll(trigger, event) {
  const galleries = document.querySelectorAll(FILTER_ENABLED_SELECTOR);

  galleries.forEach((gallery) => {
    if (!(gallery instanceof HTMLElement)) return;
    applyGalleryFilter(gallery, trigger, event);
  });
}

const galleryStateCache = new WeakMap();
let scheduledApplyTask = false;
let scheduledTrigger = '';
let scheduledEvent;

function scheduleApplyAll(trigger, event) {
  scheduledTrigger = trigger || scheduledTrigger || 'scheduled';
  if (event) scheduledEvent = event;

  if (scheduledApplyTask) return;

  scheduledApplyTask = true;
  queueMicrotask(() => {
    scheduledApplyTask = false;
    const eventToApply = scheduledEvent;
    const triggerToApply = scheduledTrigger || 'scheduled';
    scheduledEvent = undefined;
    scheduledTrigger = '';
    applyAll(triggerToApply, eventToApply);
  });
}

let isInitialized = false;

export function initVariantMediaFilter() {
  if (isInitialized) return;
  isInitialized = true;

  applyAll('init', undefined);

  EVENT_NAMES.forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      scheduleApplyAll(eventName, event);
    });
  });

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (!target.closest('variant-picker') && !target.closest('[data-product-form]') && !target.closest('.option-color-swatches')) {
        return;
      }

      scheduleApplyAll('change', event);
    },
    true
  );

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (!mutation.addedNodes.length) continue;

      const hasRelevantNode = [...mutation.addedNodes].some((node) => {
        if (!(node instanceof Element)) return false;

        return node.matches(FILTER_ENABLED_SELECTOR) || node.querySelector(FILTER_ENABLED_SELECTOR);
      });

      if (hasRelevantNode) {
        scheduleApplyAll('mutation', undefined);
        break;
      }
    }
  });

  observer.observe(document.body || document.documentElement, { childList: true, subtree: true });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  initVariantMediaFilter();
}

export const __variantMediaFilterInternal = {
  KEY_ALIASES,
  coerceOptionKey,
  containsNormalizedValue,
  resolvePrimaryEntry,
};